@if (changeDetection) {
  {{ blink() }}
}
<mat-toolbar class="logobar">
  @if (compactNavigation()) {
    <mat-toolbar-row class="compact-toolbar-row">
      <button
        mat-button
        routerLink="/"
        routerLinkActive="active"
        class="logo-button logo-button--compact"
        aria-label="SyRF home"
      >
        <mat-icon svgIcon="syrf-title" class="logo-icon" />
      </button>

      <div class="compact-toolbar-actions">
        <syrf-search-trigger [compact]="true" />
        @if (appIsUnstable()) {
          <button
            mat-icon-button
            class="unstable-warning-btn"
            aria-label="Application warning"
            matTooltip="Application may be unstable due to an error. Consider refreshing the page."
            matTooltipClass="warning-tooltip"
            (click)="showErrorRecoveryDialog()"
          >
            <mat-icon color="warn" class="warning-icon">warning</mat-icon>
          </button>
        }

        <app-environment-chip
          (clicked)="openEnvironmentWarning()"
        ></app-environment-chip>

        <button
          mat-icon-button
          [matMenuTriggerFor]="compactNavigationMenu"
          aria-label="Open navigation menu"
          data-testid="compact-navigation-menu-trigger"
        >
          <mat-icon>menu</mat-icon>
          @if (isImpersonatingUser()) {
            <span class="sr-only">Currently impersonating another user</span>
          }
        </button>
      </div>

      <mat-menu #compactNavigationMenu="matMenu">
        @if (authenticated()) {
          <button mat-menu-item [routerLink]="['projects']">
            <mat-icon>folder</mat-icon>
            <span>Projects</span>
          </button>
        }

        @if ($underProjectRoute()) {
          <button mat-menu-item [matMenuTriggerFor]="resourcesMenu">
            <mat-icon>topic</mat-icon>
            <span>Resources</span>
          </button>
        } @else {
          <a
            mat-menu-item
            href="https://www.camarades.de"
            target="_blank"
            rel="noopener noreferrer"
          >
            <mat-icon>science</mat-icon>
            <span>Methodology</span>
          </a>
          <button mat-menu-item [routerLink]="['mission']">
            <mat-icon>flag</mat-icon>
            <span>Our Mission</span>
          </button>
          <button mat-menu-item [matMenuTriggerFor]="libraryMenu">
            <mat-icon>local_library</mat-icon>
            <span>Library</span>
          </button>
          <a
            mat-menu-item
            href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
            target="_blank"
            rel="noopener noreferrer"
          >
            <mat-icon>groups</mat-icon>
            <span>About Us</span>
          </a>
        }

        <button mat-menu-item [matMenuTriggerFor]="helpMenu">
          <mat-icon>help</mat-icon>
          <span>Help</span>
        </button>

        @if (themeToggle()) {
          <button
            mat-menu-item
            [matMenuTriggerFor]="themeMenu"
            data-testid="compact-theme-menu-trigger"
          >
            <mat-icon>{{ isDark() ? 'dark_mode' : 'light_mode' }}</mat-icon>
            <span>Theme: {{ themeModeLabel() }}</span>
          </button>
        }

        @if (!authenticated()) {
          <button mat-menu-item (click)="signIn()" [disabled]="!authIsIdle()">
            @if (authIsIdle()) {
              <mat-icon>fingerprint</mat-icon>
              <span>Sign In / Register</span>
            } @else {
              <mat-spinner diameter="20"></mat-spinner>
              <span>Signing In...</span>
            }
          </button>
        }

        @if (authenticatedUser()) {
          <a mat-menu-item routerLink="manage">
            <mat-icon>account_circle</mat-icon>
            <span>My Account</span>
          </a>
          @if (stageReviewRedesign()) {
            <button
              mat-menu-item
              data-testid="compact-review-preferences-menu-item"
              (click)="openReviewPreferences()"
            >
              <mat-icon>tune</mat-icon>
              <span>Review preferences</span>
            </button>
          }
          @if (isAdmin()) {
            <button
              mat-menu-item
              [matMenuTriggerFor]="adminMenu"
              data-testid="compact-admin-menu-trigger"
            >
              <mat-icon>admin_panel_settings</mat-icon>
              <span>Admin</span>
            </button>
          }
          <button mat-menu-item (click)="signOut()">
            <mat-icon>logout</mat-icon>
            <span>Sign Out</span>
          </button>
        }
      </mat-menu>
    </mat-toolbar-row>
  }

  @if (compactNavigation() && $underProjectRoute()) {
    <mat-toolbar-row class="compact-context-row">
      <button
        mat-icon-button
        type="button"
        class="project-navigation-trigger"
        [attr.aria-expanded]="projectNavigation.open()"
        aria-controls="project-navigation-drawer"
        [attr.aria-label]="
          projectNavigation.open()
            ? 'Close project navigation'
            : 'Open project navigation'
        "
        matTooltip="Project navigation"
        data-testid="project-navigation-trigger"
        (click)="toggleProjectNavigation()"
      >
        <mat-icon>{{
          projectNavigation.open() ? 'close' : 'menu_open'
        }}</mat-icon>
      </button>

      @if (authenticated()) {
        <button
          class="compact-project-selector"
          mat-button
          [matMenuTriggerFor]="projectsMenu"
          [attr.title]="currentProject()?.name"
        >
          <mat-icon class="compact-project-icon">folder</mat-icon>
          <span class="context-prefix">Project:</span>
          <span class="context-value">
            {{ currentProject()?.name || 'Current project' }}
          </span>
          <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
        </button>
      }

      @if ($underStageRoute()) {
        <span class="compact-stage-context" [attr.title]="currentStage()?.name">
          <span class="separator" aria-hidden="true">/</span>
          <span class="context-prefix">Stage:</span>
          <span class="context-value">{{ currentStage()?.name }}</span>
        </span>
      }
    </mat-toolbar-row>
  }

  @if (!compactNavigation()) {
    <mat-toolbar-row class="wide-toolbar-row">
      <button
        mat-button
        routerLink="/"
        routerLinkActive="active"
        class="logo-button"
        aria-label="SyRF home"
      >
        <mat-icon svgIcon="syrf-title" class="logo-icon" />
      </button>
      @if (authenticated()) {
        <button
          mat-button
          class="breadcrumb-root"
          [routerLink]="['projects']"
          data-testid="nav-projects-btn"
        >
          Projects
        </button>
      }
      @if ($underProjectRoute()) {
        <span class="separator">/</span>
        @if (authenticated()) {
          <button
            class="recentProjects breadcrumb-segment"
            mat-button
            [attr.title]="currentProject()?.name"
            [matMenuTriggerFor]="projectsMenu"
            #projectsTrigger="matMenuTrigger"
            appHover
            #hover="hover"
          >
            <span class="project-name">{{ currentProject()?.name }}</span>
            <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
          </button>
        }
        @if ($underStageRoute()) {
          <span class="separator">/</span>
          <button
            mat-button
            class="stage-breadcrumb breadcrumb-segment"
            [attr.title]="currentStage()?.name"
          >
            <span class="stage-name">{{ currentStage()?.name }}</span>
          </button>
        }
      }
      @if (!$underProjectRoute()) {
        <div animate.enter="nav-slide-in" animate.leave="nav-slide-out">
          <a
            mat-button
            class="nav-button"
            href="https://www.camarades.de"
            target="_blank"
            rel="noopener noreferrer"
            >Methodology
            <mat-icon
              iconPositionEnd
              class="externalLinkIcon"
              svgIcon="external-link-white"
            />
          </a>
          <button mat-button [routerLink]="['mission']">Our Mission</button>
          <button
            [matMenuTriggerFor]="libraryMenu"
            mat-button
            matTooltip="Protocols & Systematic Review Library"
          >
            Library
          </button>
          <a
            rel="noopener noreferrer"
            mat-button
            class="nav-button"
            href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
            target="_blank"
          >
            About Us
            <mat-icon
              iconPositionEnd
              class="externalLinkIcon"
              svgIcon="external-link-white"
            />
          </a>
        </div>
      }

      <div class="menu-right">
        <syrf-search-trigger />
        @if (appIsUnstable()) {
          <button
            mat-button
            class="unstable-warning-btn"
            matTooltip="Application may be unstable due to an error. Consider refreshing the page."
            matTooltipClass="warning-tooltip"
            (click)="showErrorRecoveryDialog()"
          >
            <mat-icon color="warn" class="menuIcon warning-icon"
              >warning</mat-icon
            >
            Important!
          </button>
        }
        @if ($underProjectRoute()) {
          <div animate.enter="nav-fade-in" animate.leave="nav-fade-out">
            <button mat-button [matMenuTriggerFor]="resourcesMenu">
              <mat-icon class="menuIcon">topic</mat-icon>
              <span class="wide-label">Resources</span>
              <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
            </button>
          </div>
        }

        <!-- Environment indicator chip (shown after banner is dismissed) -->
        <app-environment-chip
          (clicked)="openEnvironmentWarning()"
        ></app-environment-chip>

        @if (themeToggle()) {
          <button
            mat-icon-button
            type="button"
            class="theme-toggle-btn"
            (click)="toggleTheme()"
            [attr.aria-label]="
              isDark() ? 'Switch to light theme' : 'Switch to dark theme'
            "
            [matTooltip]="
              isDark() ? 'Switch to light theme' : 'Switch to dark theme'
            "
            data-testid="desktop-theme-toggle-btn"
          >
            <mat-icon>{{ isDark() ? 'dark_mode' : 'light_mode' }}</mat-icon>
          </button>
        }

        <button mat-button [matMenuTriggerFor]="helpMenu">
          <mat-icon class="menuIcon">help</mat-icon>
          <span class="wide-label">Help</span>
          <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
        </button>

        @if (!authenticated()) {
          <button
            id="auth-button"
            data-testid="nav-sign-in-btn"
            mat-button
            href="javascript:void(0);"
            (click)="signIn()"
            [disabled]="!authIsIdle()"
          >
            <div class="auth-button-content">
              @if (authIsIdle()) {
                <mat-icon class="menuIcon">fingerprint</mat-icon>
                <span>Sign In / Register</span>
              } @else {
                <mat-spinner diameter="20"></mat-spinner>
                <span>Signing In...</span>
              }
            </div>
          </button>
        }
        @if (authenticatedUser(); as authenticatedUser) {
          <button
            mat-button
            [matMenuTriggerFor]="accountMenu"
            id="account-menu-trigger"
            data-testid="nav-account-menu"
          >
            @if (!profilePictureUrl()) {
              <mat-icon class="menuIcon">fingerprint</mat-icon>
            }
            <div class="account-button-content">
              @if (profilePictureUrl(); as profilePicUrl) {
                <img
                  class="menu-avatar"
                  [attr.src]="profilePicUrl"
                  alt="profile picture"
                />
              }
              <span class="account-name wide-label"
                >Hello
                {{
                  authenticatedUser.fullName?.preferred ||
                    authenticatedUser.fullName?.first
                }}</span
              >
              @if (isImpersonatingUser()) {
                <mat-icon
                  class="impersonating-icon"
                  matTooltip="Currently impersonating another user"
                  >supervisor_account</mat-icon
                >
              }
            </div>
            <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
          </button>
        }
        <mat-menu #accountMenu="matMenu">
          <a mat-menu-item routerLink="manage">My Account</a>
          @if (stageReviewRedesign()) {
            <button
              mat-menu-item
              data-testid="review-preferences-menu-item"
              (click)="openReviewPreferences()"
            >
              Review preferences
            </button>
          }
          @if (isAdmin()) {
            <button
              mat-menu-item
              [matMenuTriggerFor]="adminMenu"
              data-testid="desktop-admin-menu-trigger"
            >
              Admin
            </button>
          }
          @if (themeToggle()) {
            <button
              mat-menu-item
              [matMenuTriggerFor]="themeMenu"
              data-testid="desktop-account-theme-menu-trigger"
            >
              <mat-icon>{{ isDark() ? 'dark_mode' : 'light_mode' }}</mat-icon>
              <span>Theme: {{ themeModeLabel() }}</span>
            </button>
          }
          <button mat-menu-item (click)="signOut()">Sign Out</button>
        </mat-menu>
      </div>
    </mat-toolbar-row>
  }

  <!-- Menus shared by the compact and wide layouts. Each is declared once and
       opened by whichever layout's trigger is rendered. -->
  <mat-menu #projectsMenu="matMenu">
    @if (currentProject()?.name; as currentProjectName) {
      <div class="menu-current-project">
        <span class="menu-current-project__label">Current project</span>
        {{ currentProjectName }}
      </div>
    }
    @for (favProject of favouriteProjects(); track favProject) {
      <button
        mat-menu-item
        [routerLink]="['projects', favProject.id, 'detail']"
      >
        {{ favProject.name }}
      </button>
    }
    @for (recentProject of recentProjects(); track recentProject) {
      @if (recentProject.isMember) {
        <button
          mat-menu-item
          [routerLink]="['projects', recentProject.id, 'detail']"
        >
          {{ recentProject.name }}
        </button>
      }
    }
    <!-- Always present, so the menu can never open empty for a user with
         no current project, no favourites and no recents. -->
    <a mat-menu-item [routerLink]="['projects']">All projects</a>
  </mat-menu>

  <mat-menu #libraryMenu="matMenu">
    <button mat-menu-item [routerLink]="['library']">
      Systematic Review Library
    </button>
    <button mat-menu-item [routerLink]="['protocols']">Protocols</button>
  </mat-menu>

  <mat-menu #resourcesMenu="matMenu">
    <a
      mat-menu-item
      href="https://www.camarades.de"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="menu-item-with-icon">
        Methodology
        <mat-icon class="externalLinkIcon" svgIcon="external-link-black" />
      </div>
    </a>
    <button mat-menu-item [routerLink]="['mission']">Our Mission</button>
    <button
      [matMenuTriggerFor]="libraryMenu"
      mat-menu-item
      matTooltip="Protocols & Systematic Review Library"
    >
      Library
    </button>
    <a
      mat-menu-item
      href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="menu-item-with-icon">
        About Us
        <mat-icon class="externalLinkIcon" svgIcon="external-link-black" />
      </div>
    </a>
  </mat-menu>

  <mat-menu #helpMenu="matMenu">
    <button mat-menu-item [routerLink]="['faq']">
      Frequently Asked Questions
    </button>
    <a
      mat-menu-item
      [attr.href]="'' | userGuideUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="menu-item-with-icon">
        User Guide
        <mat-icon class="externalLinkIcon" svgIcon="external-link-black" />
      </div>
    </a>
    <button mat-menu-item [routerLink]="['about']">About Our Website</button>
    <button mat-menu-item (click)="openVersionInfo()">
      Version Information
    </button>
    <button mat-menu-item [routerLink]="['contact-us']">Contact Us</button>
    <!-- The compact menu offers Theme at its top level, so only the wide
         Help menu repeats it. -->
    @if (themeToggle() && !compactNavigation()) {
      <button
        mat-menu-item
        [matMenuTriggerFor]="themeMenu"
        data-testid="desktop-help-theme-menu-trigger"
      >
        <mat-icon>{{ isDark() ? 'dark_mode' : 'light_mode' }}</mat-icon>
        <span>Theme: {{ themeModeLabel() }}</span>
      </button>
    }
  </mat-menu>

  <mat-menu #adminMenu="matMenu">
    <button mat-menu-item (click)="toggleAdminUiOptions()">
      {{ showingAdminUiOptions() ? 'Hide' : 'Show' }} Admin UI Options
    </button>
    @if (canListUsers()) {
      <button mat-menu-item [routerLink]="['admin']">User Impersonation</button>
    }
    @if (canManageFeatureFlags()) {
      <!-- One item, two stable test ids: the id names the layout whose
           trigger opened the menu. -->
      <a
        mat-menu-item
        routerLink="/admin/feature-flags"
        [attr.data-testid]="
          compactNavigation()
            ? 'compact-admin-feature-flags'
            : 'desktop-admin-feature-flags'
        "
      >
        Feature Flags
      </a>
    }
    @if (canBatchAdminProjects()) {
      <button mat-menu-item (click)="updateAllProjectInclusion()">
        Update All Project Inclusion
      </button>
    }
    @if (currentProject()) {
      <button mat-menu-item (click)="updateAnnotationAnswerTally()">
        Update Annotation Answer Tally For Current Project
      </button>
    }
    @if (showDebuggers()) {
      <button mat-menu-item (click)="setDebuggers(false)">
        Debuggers
        <mat-icon class="menu-check">check</mat-icon>
      </button>
    } @else {
      <button mat-menu-item (click)="setDebuggers(true)">Debuggers</button>
    }
    @if (showLogging()) {
      <button mat-menu-item (click)="setLogging(false)">
        Diagnostics
        <mat-icon class="menu-check">check</mat-icon>
      </button>
    } @else {
      <button mat-menu-item (click)="setLogging(true)">Diagnostics</button>
    }
    @if (showChangeDetection()) {
      <button mat-menu-item (click)="setChangeDetectionVisualisation(false)">
        Change Detection Visualisation
        <mat-icon class="menu-check">check</mat-icon>
      </button>
    } @else {
      <button mat-menu-item (click)="setChangeDetectionVisualisation(true)">
        Change Detection Visualisation
      </button>
    }
    <button mat-menu-item (click)="openGuidDecoder()">
      Guid Decoder/Encoder...
    </button>
    <button mat-menu-item (click)="onThrowError()">Throw Error</button>
    <button mat-menu-item disabled>{{ randomId }}</button>
  </mat-menu>

  <mat-menu #themeMenu="matMenu">
    <button
      mat-menu-item
      (click)="setThemeMode('light')"
      data-testid="theme-option-light"
    >
      <mat-icon>light_mode</mat-icon>
      <span>Light</span>
      @if (themeMode() === 'light') {
        <mat-icon class="menu-check">check</mat-icon>
      }
    </button>
    <button
      mat-menu-item
      (click)="setThemeMode('dark')"
      data-testid="theme-option-dark"
    >
      <mat-icon>dark_mode</mat-icon>
      <span>Dark</span>
      @if (themeMode() === 'dark') {
        <mat-icon class="menu-check">check</mat-icon>
      }
    </button>
    <button
      mat-menu-item
      (click)="setThemeMode('system')"
      data-testid="theme-option-system"
    >
      <mat-icon>settings_brightness</mat-icon>
      <span>System preference</span>
      @if (themeMode() === 'system') {
        <mat-icon class="menu-check">check</mat-icon>
      }
    </button>
  </mat-menu>
</mat-toolbar>
